import React from 'react';
import { FONTS } from '../../styles/tokens';
import type { DeployEnvironment } from '../../hooks/useDeployEnvironment';

interface EnvironmentBannerProps {
  env: DeployEnvironment;
  colors: { text: string; textSecondary: string; border: string };
}

interface BannerStyle {
  title: string;
  accent: string;
  background: string;
}

/**
 * Color + title map per environment. Exported so the Hub header can reuse
 * the accent color for a small env chip if we add one later.
 */
const STYLES: Record<'dev' | 'staging' | 'prod', BannerStyle> = {
  dev:     { title: 'Local Dev',  accent: '#4ADE80', background: 'rgba(74,222,128,0.10)' },
  staging: { title: 'Staging',    accent: '#60A5FA', background: 'rgba(96,165,250,0.12)' },
  prod:    { title: 'Production', accent: '#EF4444', background: 'rgba(239,68,68,0.12)' },
};

/**
 * Environment-aware banner that replaces the old SimulationBanner. Shows a
 * different color + label per `VERTEX_ENV`, and only lists "simulated" notes
 * when something is actually fake (no k8s, no GCP). In real local-kind dev,
 * none of those notes appear.
 */
export function EnvironmentBanner(props: EnvironmentBannerProps): React.JSX.Element | null {
  const style = STYLES[props.env.env];
  const notes = buildNotes(props.env);

  if (props.env.env === 'dev' && notes.length === 0) {
    return renderBanner(style, ['Using your local kind cluster. Builds run via `docker build` + `kind load`.'], props.colors);
  }
  if (notes.length === 0 && props.env.env !== 'prod') {
    return renderBanner(style, [`Deploys will target the ${props.env.env} infrastructure.`], props.colors);
  }
  if (notes.length === 0) return null;
  return renderBanner(style, notes, props.colors);
}

/** Build caveat messages based on which parts of the stack are simulated. */
function buildNotes(env: DeployEnvironment): string[] {
  const out: string[] = [];
  if (env.simulatedGcp && !env.localBuild) {
    out.push('GCP Cloud Build is simulated — no real container image will be built or pushed.');
  }
  if (env.simulatedK8s) {
    out.push(`Kubernetes is simulated — no real pod will run, and https://<slug>.${env.appsDomain} will not resolve.`);
  }
  if (out.length > 0) {
    out.push('Deploys will succeed in the UI, but the resulting URL is not live until a real cluster is wired up.');
  }
  return out;
}

/** Render the banner shell with a dynamic color + title. */
function renderBanner(
  style: BannerStyle,
  messages: string[],
  colors: { text: string; textSecondary: string },
): React.JSX.Element {
  return (
    <div
      role="status"
      style={{
        marginBottom: '16px',
        padding: '12px 16px',
        borderRadius: '10px',
        border: `1px solid ${style.accent}66`,
        backgroundColor: style.background,
        color: colors.text,
      }}
    >
      <div style={{ fontFamily: FONTS.heading, fontSize: '11px', letterSpacing: '0.15em', textTransform: 'uppercase', color: style.accent, marginBottom: '6px' }}>
        {style.title}
      </div>
      <ul style={{ margin: 0, paddingLeft: '18px' }}>
        {messages.map((msg, i) => (
          <li key={i} style={{ fontFamily: FONTS.body, fontSize: '12px', color: colors.textSecondary, padding: '2px 0' }}>
            {msg}
          </li>
        ))}
      </ul>
    </div>
  );
}
